import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import '../style.css';

const API = 'https://culturlingua.com/blog_api.php';

/* Safari-safe hex darkening — replaces color-mix() */
function darkenHex(hex, amount = 0.35) {
  const h = hex.replace('#', '');
  const num = parseInt(h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h, 16);
  const r = Math.max(0, Math.round(((num >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.round(((num >> 8)  & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.round(( num        & 0xff) * (1 - amount)));
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

function formatDate(str) {
  return new Date(str).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
}

/* Card visual header — accent strip + emoji pill, matches modal-card style */
function CardVisual({ color, icon, category }) {
  const c1 = color || '#3b6fe0';
  const darken = darkenHex(c1, 0.28);
  const initials = (category || 'Ge').substring(0, 2).toUpperCase();
  return (
    <div style={{ position: 'relative', paddingTop: '5px', background: 'white' }}>
      {/* 5px accent strip — same language as modal-card-accent */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '5px',
        background: `linear-gradient(90deg, ${c1}, ${darken})`,
      }} />
      {/* Emoji + category label row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '18px 22px 6px',
      }}>
        <div style={{
          width: '52px', height: '52px', borderRadius: '14px',
          background: `${c1}14`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, fontSize: '26px', lineHeight: 1,
        }}>
          {icon || (
            <span style={{
              fontFamily: "'Fraunces',serif", fontSize: '1.2rem', fontWeight: 900,
              color: c1, lineHeight: 1, letterSpacing: '-1px',
            }}>{initials}</span>
          )}
        </div>
        <span style={{
          fontSize: '10px', fontWeight: 800, letterSpacing: '.08em',
          textTransform: 'uppercase', color: c1,
        }}>{category}</span>
      </div>
    </div>
  );
}

/* Featured card left panel — cream bg, accent strip, large emoji */
function FeaturedVisual({ color, icon, category }) {
  const c1 = color || '#1a1744';
  const darken = darkenHex(c1, 0.30);
  const initials = (category || 'Ge').substring(0, 2).toUpperCase();
  return (
    <div style={{
      background: '#faf8f3',
      position: 'relative', overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '240px',
    }}>
      {/* Accent strip top */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '5px',
        background: `linear-gradient(90deg, ${c1}, ${darken})`,
      }} />
      {/* Very faint radial tint for depth */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(ellipse at 55% 45%, ${c1}0a 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      {icon ? (
        <span style={{
          fontSize: '80px', position: 'relative', zIndex: 2, lineHeight: 1,
          filter: `drop-shadow(0 4px 16px ${c1}30)`,
        }}>{icon}</span>
      ) : (
        <span style={{
          fontFamily: "'Fraunces',serif", fontSize: '5rem', fontWeight: 900,
          color: `${c1}22`, position: 'relative', zIndex: 2,
          lineHeight: 1, letterSpacing: '-4px', userSelect: 'none',
        }}>{initials}</span>
      )}
    </div>
  );
}

const S = `
@keyframes spin    { to { transform:rotate(360deg) } }
@keyframes fadeUp  { from { opacity:0; transform:translateY(18px) } to { opacity:1; transform:none } }
@keyframes shimmer { from { background-position:-200% 0 } to { background-position:200% 0 } }
@keyframes bounceY {
  0%,100% { transform:translateY(0)   opacity:.55; }
  50%      { transform:translateY(8px); opacity:1; }
}
@keyframes fadeOutDown {
  to { opacity:0; transform:translateY(14px); pointer-events:none; }
}

/* ── Hero ── */
.bl-hero { background:var(--navy); position:relative; overflow:hidden; }
.bl-hero-orb1 { position:absolute; top:-100px; right:-80px; width:480px; height:480px; background:radial-gradient(circle,rgba(245,200,66,.07) 0%,transparent 68%); pointer-events:none; }
.bl-hero-orb2 { position:absolute; bottom:-60px; left:-80px; width:360px; height:360px; background:radial-gradient(circle,rgba(59,111,224,.09) 0%,transparent 68%); pointer-events:none; }
.bl-hero-orb3 { position:absolute; top:40%; left:40%; transform:translate(-50%,-50%); width:600px; height:600px; background:radial-gradient(circle,rgba(255,255,255,.015) 0%,transparent 70%); pointer-events:none; }

.bl-hero-inner { max-width:820px; margin:0 auto; padding:88px 48px 64px; text-align:center; position:relative; z-index:2; animation:fadeUp .7s ease both; }

.bl-eyebrow { display:inline-flex; align-items:center; gap:10px; font-size:.7rem; font-weight:800; letter-spacing:.14em; text-transform:uppercase; color:var(--gold); margin-bottom:22px; }
.bl-eyebrow::before,.bl-eyebrow::after { content:''; width:32px; height:1px; background:currentColor; opacity:.5; }

.bl-h1 { font-family:'Fraunces',serif; font-size:clamp(2.4rem,5vw,3.8rem); font-weight:900; color:white; line-height:1.05; letter-spacing:-1.5px; margin-bottom:20px; }
.bl-h1 em { font-style:italic; font-weight:300; color:var(--gold); }

.bl-sub { color:rgba(255,255,255,.52); font-size:1rem; line-height:1.75; max-width:460px; margin:0 auto 36px; }

/* Stats */
.bl-stats { display:flex; gap:0; justify-content:center; margin-bottom:40px; }
.bl-stat { padding:0 28px; text-align:center; position:relative; }
.bl-stat + .bl-stat::before { content:''; position:absolute; left:0; top:10%; height:80%; width:1px; background:rgba(255,255,255,.1); }
.bl-stat-n { font-family:'Fraunces',serif; font-size:1.7rem; font-weight:900; color:white; line-height:1; }
.bl-stat-l { font-size:.7rem; color:rgba(255,255,255,.38); margin-top:4px; letter-spacing:.04em; }

.bl-search-wrap { max-width:400px; margin:0 auto; position:relative; }
.bl-search { width:100%; padding:14px 20px 14px 48px; border-radius:100px; border:1.5px solid rgba(255,255,255,.13); background:rgba(255,255,255,.07); color:white; font-size:.93rem; font-family:'DM Sans',sans-serif; outline:none; box-sizing:border-box; transition:border-color .25s,background .25s,box-shadow .25s; }
.bl-search::placeholder { color:rgba(255,255,255,.28); }
.bl-search:focus { border-color:rgba(245,200,66,.5); background:rgba(255,255,255,.1); box-shadow:0 0 0 4px rgba(245,200,66,.07); }
.bl-search-ico { position:absolute; left:17px; top:50%; transform:translateY(-50%); opacity:.32; pointer-events:none; }

/* ── Scroll hint (vertical, below hero) ── */
.bl-scroll-hint {
  display:flex; flex-direction:column; align-items:center; gap:8px;
  padding:28px 0 8px; position:relative; z-index:3;
  transition:opacity .35s ease, transform .35s ease;
}
.bl-scroll-hint.hide {
  opacity:0; transform:translateY(10px); pointer-events:none;
}
.bl-scroll-hint-label {
  font-size:.65rem; font-weight:700; letter-spacing:.13em; text-transform:uppercase;
  color:rgba(255,255,255,.35);
}
.bl-scroll-hint-track {
  width:24px; height:38px; border-radius:12px;
  border:1.5px solid rgba(255,255,255,.2);
  display:flex; align-items:flex-start; justify-content:center;
  padding-top:5px; box-sizing:border-box;
}
.bl-scroll-hint-dot {
  width:5px; height:5px; border-radius:50%;
  background:rgba(255,255,255,.55);
  animation:bounceY 1.6s ease infinite;
}

/* ── Category tabs ── */
.bl-catbar { background:white; border-bottom:1px solid #ede9e0; padding:0 48px; display:flex; gap:0; justify-content:center; overflow-x:auto; scrollbar-width:none; position:sticky; top:72px; z-index:10; box-shadow:0 2px 12px rgba(0,0,0,.04); }
.bl-catbar::-webkit-scrollbar { display:none; }
.bl-catbtn { padding:16px 22px; border:none; border-bottom:3px solid transparent; background:transparent; color:#aaa; font-family:'DM Sans',sans-serif; font-weight:700; font-size:.82rem; cursor:pointer; white-space:nowrap; transition:color .2s,border-color .2s; letter-spacing:.02em; }
.bl-catbtn:hover { color:var(--navy); }
.bl-catbtn.on { color:var(--navy); border-bottom-color:var(--gold); }

/* ── Content area ── */
.bl-area { background:var(--cream); padding:52px 48px 88px; min-height:500px; }
.bl-inner { max-width:1100px; margin:0 auto; }

.bl-sec-lbl { font-size:.66rem; font-weight:800; letter-spacing:.14em; text-transform:uppercase; color:#c0b8a8; margin-bottom:20px; display:flex; align-items:center; gap:12px; }
.bl-sec-lbl::after { content:''; flex:1; height:1px; background:#e8e2d8; }

/* ── Featured card — more compact ── */
.bl-feat { display:grid; grid-template-columns:380px 1fr; background:white; border-radius:20px; overflow:hidden; box-shadow:0 4px 24px rgba(26,23,68,.08); margin-bottom:44px; text-decoration:none; transition:transform .35s cubic-bezier(.23,1,.32,1),box-shadow .35s ease; animation:fadeUp .55s ease both .08s; }
.bl-feat:hover { transform:translateY(-5px); box-shadow:0 20px 56px rgba(26,23,68,.14); }
.bl-feat-r { padding:36px 40px; display:flex; flex-direction:column; justify-content:center; }
.bl-feat-badge { display:inline-flex; align-items:center; gap:6px; background:rgba(245,200,66,.1); color:#7a5e00; font-size:.68rem; font-weight:800; padding:4px 12px; border-radius:100px; margin-bottom:16px; width:fit-content; letter-spacing:.04em; border:1px solid rgba(245,200,66,.22); }
.bl-feat-title { font-family:'Fraunces',serif; font-size:clamp(1.25rem,2vw,1.7rem); font-weight:900; color:var(--navy); line-height:1.22; letter-spacing:-.2px; margin-bottom:14px; }
.bl-feat-exc { color:#777; line-height:1.75; font-size:.93rem; margin-bottom:22px; flex:1; display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; }
.bl-feat-foot { display:flex; justify-content:space-between; align-items:center; padding-top:16px; border-top:1px solid #f0ede8; }
.bl-feat-meta { display:flex; gap:14px; flex-wrap:wrap; }
.bl-feat-meta span { color:#c0b8a8; font-size:.75rem; }
.bl-feat-cta { display:inline-flex; align-items:center; gap:6px; color:var(--blue); font-weight:800; font-size:.88rem; white-space:nowrap; transition:gap .2s; }
.bl-feat:hover .bl-feat-cta { gap:12px; }

/* ── Grid cards ── */
.bl-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(290px,1fr)); gap:20px; }
.bl-card { background:white; border-radius:18px; overflow:hidden; box-shadow:0 2px 12px rgba(26,23,68,.05); transition:transform .3s cubic-bezier(.23,1,.32,1),box-shadow .3s ease; display:flex; flex-direction:column; text-decoration:none; animation:fadeUp .45s ease both; }
.bl-card:hover { transform:translateY(-5px); box-shadow:0 16px 40px rgba(26,23,68,.11); }
.bl-card-body { padding:10px 22px 18px; flex:1; display:flex; flex-direction:column; }
.bl-card-title { font-family:'Fraunces',serif; font-size:1.04rem; font-weight:900; color:var(--navy); line-height:1.3; margin-bottom:8px; flex:1; }
.bl-card-exc { color:#999; font-size:.82rem; line-height:1.6; margin-bottom:16px; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.bl-card-ft { display:flex; justify-content:space-between; align-items:center; padding-top:12px; border-top:1px solid #f4f1ec; }
.bl-card-date { color:#c8c0b4; font-size:.73rem; }
.bl-card-read { color:var(--blue); font-weight:700; font-size:.8rem; display:inline-flex; align-items:center; gap:4px; transition:gap .18s; }
.bl-card:hover .bl-card-read { gap:8px; }

/* ── Skeleton ── */
.bl-skel { border-radius:18px; overflow:hidden; background:white; box-shadow:0 2px 12px rgba(26,23,68,.04); }
.bl-skel-img { height:140px; background:linear-gradient(90deg,#f0ede8 25%,#e8e2d8 50%,#f0ede8 75%); background-size:200% 100%; animation:shimmer 1.4s ease infinite; }
.bl-skel-body { padding:22px 24px; }
.bl-skel-line { border-radius:6px; background:linear-gradient(90deg,#f0ede8 25%,#e8e2d8 50%,#f0ede8 75%); background-size:200% 100%; animation:shimmer 1.4s ease infinite; margin-bottom:10px; }

.bl-empty { text-align:center; padding:80px 0; }
.bl-spinner { display:inline-block; width:34px; height:34px; border:3px solid #e5e7eb; border-top-color:var(--blue); border-radius:50%; animation:spin .8s linear infinite; }

/* ── Responsive ── */
@media (max-width:860px) {
  .bl-feat { grid-template-columns:1fr; }
  .bl-hero-inner,.bl-area { padding-left:20px; padding-right:20px; }
  .bl-catbar { padding:0 12px; }
  .bl-stats { gap:0; }
  .bl-stat { padding:0 16px; }
}
@media (max-width:600px) {
  .bl-grid { grid-template-columns:1fr; }
  .bl-feat-r { padding:24px 22px; }
  .bl-stat-n { font-size:1.4rem; }
}
`;


function ScrollHint() {
  const [hidden, setHidden] = React.useState(false);
  React.useEffect(() => {
    const handler = () => { if (window.scrollY > 60) setHidden(true); };
    window.addEventListener('scroll', handler, { passive: true });
    const t = setTimeout(() => setHidden(true), 5000);
    return () => { window.removeEventListener('scroll', handler); clearTimeout(t); };
  }, []);
  return (
    <div className={`bl-scroll-hint${hidden ? ' hide' : ''}`} aria-hidden="true">
      <span className="bl-scroll-hint-label">Ver artículos</span>
      <div className="bl-scroll-hint-track">
        <div className="bl-scroll-hint-dot" />
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bl-skel">
      <div className="bl-skel-img" />
      <div className="bl-skel-body">
        <div className="bl-skel-line" style={{height:'10px', width:'40%'}} />
        <div className="bl-skel-line" style={{height:'18px', width:'85%'}} />
        <div className="bl-skel-line" style={{height:'18px', width:'70%'}} />
        <div className="bl-skel-line" style={{height:'12px', width:'55%', marginTop:'8px'}} />
      </div>
    </div>
  );
}

export default function Blog() {
  const [posts, setPosts]           = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActive] = useState('Todos');
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(true);
  const searchRef                   = useRef(null);

  useEffect(() => {
    fetch(`${API}?action=categories`)
      .then(r => r.json()).then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const cat = activeCategory !== 'Todos' ? `&category=${encodeURIComponent(activeCategory)}` : '';
    const q   = search ? `&search=${encodeURIComponent(search)}` : '';
    let cancelled = false;
    const delay = search ? 300 : 0;
    const timer = setTimeout(() => {
      fetch(`${API}?action=posts${cat}${q}`)
        .then(r => r.json())
        .then(data => {
          if (!cancelled) {
            setPosts(Array.isArray(data) ? data : []);
            setLoading(false);
          }
        })
        .catch(() => { if (!cancelled) setLoading(false); });
    }, delay);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [activeCategory, search]);

  const allCats  = ['Todos', ...categories.map(c => c.category)];
  const featured = posts[0];
  const rest     = posts.slice(1);

  return (
    <div className="page-wrapper">
      <style>{S}</style>

      <Helmet>
        <title>Blog de idiomas | Culturlingua</title>
        <meta name="description" content="Consejos para aprender inglés y español más rápido. Guías, tips y recursos del blog de Culturlingua." />
        <meta property="og:title" content="Blog de idiomas | Culturlingua" />
        <meta property="og:description" content="Consejos para aprender inglés y español más rápido." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://culturlingua.com/blog" />
        <meta property="og:image" content="https://culturlingua.com/blog_images/blog-default.jpg" />
        <meta property="og:site_name" content="Culturlingua" />
        <link rel="canonical" href="https://culturlingua.com/blog" />
        <script type="application/ld+json">{JSON.stringify({
          "@context":"https://schema.org","@type":"Blog",
          "name":"Blog Culturlingua","url":"https://culturlingua.com/blog",
          "description":"Consejos, guías y recursos para aprender idiomas más rápido.",
          "publisher":{"@type":"Organization","name":"Culturlingua",
            "logo":{"@type":"ImageObject","url":"https://culturlingua.com/logo.png"}}
        })}</script>
      </Helmet>

      {/* ── Hero ── */}
      <div className="bl-hero">
        <div className="bl-hero-orb1"/><div className="bl-hero-orb2"/><div className="bl-hero-orb3"/>
        <div className="bl-hero-inner">
          <div className="bl-eyebrow">Blog Culturlingua</div>
          <h1 className="bl-h1">
            Aprende idiomas<br /><em>más rápido</em>
          </h1>
          <p className="bl-sub">
            Consejos, guías y recursos escritos por expertos para acelerar tu aprendizaje.
          </p>

          {/* Stats */}
          <div className="bl-stats">
            {[
              { n: posts.length || '…', l: 'Artículos' },
              { n: categories.length || '…', l: 'Categorías' },
              { n: '100%', l: 'Gratis' },
            ].map(s => (
              <div key={s.l} className="bl-stat">
                <div className="bl-stat-n">{s.n}</div>
                <div className="bl-stat-l">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="bl-search-wrap">
            <span className="bl-search-ico">🔍</span>
            <input
              ref={searchRef}
              className="bl-search"
              type="search"
              placeholder="Buscar artículos…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <ScrollHint />
        </div>
      </div>

      {/* ── Category tabs ── */}
      {allCats.length > 1 && (
        <div className="bl-catbar">
          {allCats.map(cat => (
            <button key={cat}
              className={`bl-catbtn${activeCategory === cat ? ' on' : ''}`}
              onClick={() => setActive(cat)}>
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* ── Content ── */}
      <div className="bl-area">
        <div className="bl-inner">

          {/* Skeletons */}
          {loading && (
            <>
              <div className="bl-sec-lbl">Artículo destacado</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',background:'white',
                borderRadius:'24px',overflow:'hidden',marginBottom:'48px',
                boxShadow:'0 4px 32px rgba(26,23,68,.08)'}}>
                <div className="bl-skel-img" style={{height:'340px'}}/>
                <div style={{padding:'52px 48px'}}>
                  {[80,90,60,40,30].map((w,i)=>(
                    <div key={i} className="bl-skel-line"
                      style={{height: i===0?10:i<3?20:12, width:`${w}%`, marginBottom:'14px'}}/>
                  ))}
                </div>
              </div>
              <div className="bl-sec-lbl">Más artículos</div>
              <div className="bl-grid">
                {[1,2,3].map(i => <SkeletonCard key={i}/>)}
              </div>
            </>
          )}

          {/* Empty */}
          {!loading && posts.length === 0 && (
            <div className="bl-empty">
              <div style={{fontSize:'3rem',marginBottom:'12px'}}>📝</div>
              <h3 style={{fontFamily:"'Fraunces',serif",color:'var(--navy)',marginBottom:'8px'}}>
                Sin artículos{search ? ` para "${search}"` : ' aún'}
              </h3>
              <p style={{color:'#aaa'}}>
                {search ? 'Intenta con otras palabras.' : 'Vuelve pronto para nuevo contenido.'}
              </p>
            </div>
          )}

          {/* Featured */}
          {!loading && featured && (
            <>
              <div className="bl-sec-lbl">Artículo destacado</div>
              <Link to={'/blog/' + featured.slug} className="bl-feat">
                <FeaturedVisual
                  color={featured.category_color}
                  icon={featured.icon}
                  category={featured.category}
                />
                <div className="bl-feat-r">
                  <div className="bl-feat-badge">⭐ Artículo destacado</div>
                  <h2 className="bl-feat-title">{featured.title}</h2>
                  <p className="bl-feat-exc">{featured.excerpt}</p>
                  <div className="bl-feat-foot">
                    <div className="bl-feat-meta">
                      <span>📅 {formatDate(featured.created_at)}</span>
                      <span>⏱ {featured.read_time}</span>
                    </div>
                    <span className="bl-feat-cta">Leer <span>→</span></span>
                  </div>
                </div>
              </Link>
            </>
          )}

          {/* Grid */}
          {!loading && rest.length > 0 && (
            <>
              <div className="bl-sec-lbl" style={{marginTop:'8px'}}>Más artículos</div>
              <div className="bl-grid">
                {rest.map((post, i) => (
                  <Link key={post.slug} to={'/blog/' + post.slug}
                    className="bl-card"
                    style={{ animationDelay: `${0.06 * i}s` }}>
                    <CardVisual
                      color={post.category_color}
                      icon={post.icon}
                      category={post.category}
                    />
                    <div className="bl-card-body">
                      <h3 className="bl-card-title">{post.title}</h3>
                      <p className="bl-card-exc">
                        {(post.excerpt || '').length > 110
                          ? post.excerpt.slice(0, 110) + '…'
                          : post.excerpt}
                      </p>
                      <div className="bl-card-ft">
                        <span className="bl-card-date">{formatDate(post.created_at)}</span>
                        <span className="bl-card-read">{post.read_time} <span>→</span></span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}